import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Palmtree, Plus, ScrollText, Settings2, Tags, Trash2 } from "lucide-react";
import { papi, errText } from "../portalApi";
import { usePortal } from "../PortalApp";
import { Card, Field, Modal, PageHeader, Spinner, fmtDate, fmtTime, hasPerm, inputCls } from "../ui";

const TABS = [
  ["holidays", "Holidays", Palmtree],
  ["leave-types", "Leave Types", Tags],
  ["announcements", "Announcements", Megaphone],
  ["settings", "Settings", Settings2],
  ["audit", "Audit Log", ScrollText, "audit_view"],
];

const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function HRAdmin() {
  const { emp: me } = usePortal();
  const canManage = hasPerm(me, "hr_manage");
  const [tab, setTab] = useState("holidays");
  const [holidays, setHolidays] = useState([]);
  const [types, setTypes] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [settings, setSettings] = useState(null);
  const [audit, setAudit] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [modal, setModal] = useState(null); // 'holiday' | 'leave-type' | 'announcement'
  const [hForm, setHForm] = useState({ date: "", name: "", type: "Company Holiday", notifyAll: true });
  const [ltForm, setLtForm] = useState({ code: "", name: "", annual: 0, paid: true });
  const [aForm, setAForm] = useState({ title: "", body: "", category: "General", audience: "Everyone" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const reqs = [papi.get("/holidays"), papi.get("/leave/types"), papi.get("/announcements"), papi.get("/meta")];
      if (hasPerm(me, "hr_manage") || hasPerm(me, "hr_view")) reqs.push(papi.get("/hr/settings"));
      if (hasPerm(me, "audit_view")) reqs.push(papi.get("/hr/audit-logs"));
      const [h, lt, an, m, s, au] = await Promise.all(reqs);
      setHolidays(h.data);
      setTypes(lt.data);
      setAnnouncements(an.data);
      setMeta(m.data);
      if (s) setSettings({ ...s.data, approvedDomains: (s.data.approvedDomains || []).join(", ") });
      if (au) setAudit(au.data);
      setLoaded(true);
    } catch (err) {
      toast.error(errText(err, "Could not load HR administration data."));
    }
  }, [me]);

  useEffect(() => { load(); }, [load]);

  if (!loaded || !meta) return <Spinner />;

  const addHoliday = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await papi.post("/hr/holidays", hForm);
      toast.success("Holiday added.");
      setModal(null);
      setHForm({ date: "", name: "", type: "Company Holiday", notifyAll: true });
      load();
    } catch (err) {
      toast.error(errText(err, "Could not add the holiday."));
    } finally {
      setSaving(false);
    }
  };

  const deleteHoliday = async (h) => {
    if (!window.confirm(`Delete ${h.name} (${h.date})?`)) return;
    try {
      await papi.delete(`/hr/holidays/${h.id}`);
      toast.success("Holiday removed.");
      load();
    } catch (err) {
      toast.error(errText(err, "Could not delete the holiday."));
    }
  };

  const addType = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await papi.post("/hr/leave-types", ltForm);
      toast.success("Leave type created and balances allocated to all employees.");
      setModal(null);
      setLtForm({ code: "", name: "", annual: 0, paid: true });
      load();
    } catch (err) {
      toast.error(errText(err, "Could not create the leave type."));
    } finally {
      setSaving(false);
    }
  };

  const addAnnouncement = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await papi.post("/hr/announcements", aForm);
      toast.success("Announcement published and employees notified.");
      setModal(null);
      setAForm({ title: "", body: "", category: "General", audience: "Everyone" });
      load();
    } catch (err) {
      toast.error(errText(err, "Could not publish the announcement."));
    } finally {
      setSaving(false);
    }
  };

  const deleteAnnouncement = async (a) => {
    if (!window.confirm(`Remove announcement "${a.title}"? Employees will no longer see it.`)) return;
    try {
      await papi.delete(`/hr/announcements/${a.id}`);
      toast.success("Announcement removed.");
      load();
    } catch (err) {
      toast.error(errText(err, "Could not remove the announcement."));
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await papi.put("/hr/settings", {
        workDays: settings.workDays,
        weeklyOff: settings.weeklyOff,
        shiftStart: settings.shiftStart,
        shiftEnd: settings.shiftEnd,
        graceMinutes: parseInt(settings.graceMinutes, 10) || 0,
        halfDayHours: parseFloat(settings.halfDayHours) || 0,
        fullDayHours: parseFloat(settings.fullDayHours) || 0,
        approvedDomains: settings.approvedDomains.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Settings saved.");
      load();
    } catch (err) {
      toast.error(errText(err, "Could not save settings."));
    } finally {
      setSaving(false);
    }
  };

  const toggleWorkDay = (d) => {
    const has = settings.workDays.includes(d);
    setSettings({ ...settings, workDays: has ? settings.workDays.filter((x) => x !== d) : [...settings.workDays, d] });
  };

  const visibleTabs = TABS.filter((t) => !t[3] || hasPerm(me, t[3]));

  return (
    <div data-testid="hr-admin-page">
      <PageHeader title="HR Administration" sub="Holidays, leave policies, announcements, portal settings and the audit trail." />

      <div className="mb-6 flex flex-wrap gap-2" data-testid="hr-tabs">
        {visibleTabs.map(([key, label, Icon]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
              tab === key ? "border-gold bg-gold/10 text-gold-dark" : "border-navy-900/15 bg-white text-slate-500 hover:border-gold/50"
            }`}
            data-testid={`hr-tab-${key}`}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden /> {label}
          </button>
        ))}
      </div>

      {tab === "holidays" && (
        <div data-testid="hr-holidays">
          {canManage && (
            <div className="mb-4 flex justify-end">
              <button onClick={() => setModal("holiday")} className="btn-gold !px-5 !py-2.5 !text-xs" data-testid="add-holiday-btn">
                <Plus className="h-4 w-4" aria-hidden /> Add Holiday
              </button>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {holidays.map((h) => (
              <Card key={h.id} className="!p-5" testid={`holiday-${h.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-serif text-lg text-navy-900">{h.name}</p>
                    <p className="mt-1 text-xs font-semibold text-gold-dark">{fmtDate(h.date)}</p>
                    <p className="mt-1 text-[11px] uppercase tracking-wider text-slate-400">{h.type}</p>
                  </div>
                  {canManage && (
                    <button onClick={() => deleteHoliday(h)} className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600" aria-label={`Delete ${h.name}`} data-testid={`delete-holiday-${h.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {tab === "leave-types" && (
        <div data-testid="hr-leave-types">
          {canManage && (
            <div className="mb-4 flex justify-end">
              <button onClick={() => setModal("leave-type")} className="btn-gold !px-5 !py-2.5 !text-xs" data-testid="add-leave-type-btn">
                <Plus className="h-4 w-4" aria-hidden /> Add Leave Type
              </button>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {types.map((t) => (
              <Card key={t.id} className="!p-5" testid={`leave-type-${t.code}`}>
                <div className="flex items-center justify-between">
                  <p className="font-serif text-lg text-navy-900">{t.name}</p>
                  <span className="rounded-full bg-navy-950 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-gold">{t.code}</span>
                </div>
                <p className="mt-2 text-sm text-slate-500">
                  {t.annual} day(s) per year · <span className="font-semibold">{t.paid ? "Paid" : "Unpaid"}</span>
                </p>
              </Card>
            ))}
          </div>
        </div>
      )}

      {tab === "announcements" && (
        <div data-testid="hr-announcements">
          {canManage && (
            <div className="mb-4 flex justify-end">
              <button onClick={() => setModal("announcement")} className="btn-gold !px-5 !py-2.5 !text-xs" data-testid="add-announcement-btn">
                <Plus className="h-4 w-4" aria-hidden /> New Announcement
              </button>
            </div>
          )}
          <div className="space-y-3">
            {announcements.map((a) => (
              <Card key={a.id} className="!p-5" testid={`announcement-${a.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-navy-900">{a.title}</p>
                      <span className="rounded-full bg-gold/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gold-dark">{a.category}</span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{a.audience}</span>
                    </div>
                    <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{a.body}</p>
                    <p className="mt-2 text-[10px] uppercase tracking-wider text-slate-400">By {a.createdBy} · {fmtDate(a.createdAt)}</p>
                  </div>
                  {canManage && (
                    <button onClick={() => deleteAnnouncement(a)} className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600" aria-label={`Remove ${a.title}`} data-testid={`delete-announcement-${a.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {tab === "settings" && settings && (
        <Card className="max-w-2xl" testid="hr-settings">
          <form onSubmit={saveSettings} className="space-y-5" data-testid="settings-form">
            <div>
              <p className={labelCls}>Working Days</p>
              <div className="flex flex-wrap gap-2" data-testid="settings-workdays">
                {WEEK_DAYS.map((d) => (
                  <button
                    type="button"
                    key={d}
                    onClick={() => canManage && toggleWorkDay(d)}
                    disabled={!canManage}
                    className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                      settings.workDays.includes(d) ? "border-gold bg-gold/10 text-gold-dark" : "border-navy-900/15 bg-white text-slate-400"
                    }`}
                    data-testid={`workday-${d}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Field label="Weekly Off">
                <select className={inputCls} value={settings.weeklyOff} onChange={(e) => setSettings({ ...settings, weeklyOff: e.target.value })} disabled={!canManage} data-testid="settings-weekly-off">
                  {WEEK_DAYS.map((d) => <option key={d}>{d}</option>)}
                </select>
              </Field>
              <Field label="Shift Start">
                <input type="time" className={inputCls} value={settings.shiftStart} onChange={(e) => setSettings({ ...settings, shiftStart: e.target.value })} disabled={!canManage} data-testid="settings-shift-start" />
              </Field>
              <Field label="Shift End">
                <input type="time" className={inputCls} value={settings.shiftEnd} onChange={(e) => setSettings({ ...settings, shiftEnd: e.target.value })} disabled={!canManage} data-testid="settings-shift-end" />
              </Field>
              <Field label="Grace (min)">
                <input type="number" min="0" className={inputCls} value={settings.graceMinutes} onChange={(e) => setSettings({ ...settings, graceMinutes: e.target.value })} disabled={!canManage} data-testid="settings-grace" />
              </Field>
              <Field label="Half-Day Hours">
                <input type="number" min="0" step="0.5" className={inputCls} value={settings.halfDayHours} onChange={(e) => setSettings({ ...settings, halfDayHours: e.target.value })} disabled={!canManage} data-testid="settings-halfday" />
              </Field>
              <Field label="Full-Day Hours">
                <input type="number" min="0" step="0.5" className={inputCls} value={settings.fullDayHours} onChange={(e) => setSettings({ ...settings, fullDayHours: e.target.value })} disabled={!canManage} data-testid="settings-fullday" />
              </Field>
            </div>
            <Field label="Approved Email Domains (comma separated)">
              <input className={inputCls} value={settings.approvedDomains} onChange={(e) => setSettings({ ...settings, approvedDomains: e.target.value })} disabled={!canManage} data-testid="settings-domains" />
            </Field>
            {canManage && (
              <button type="submit" disabled={saving} className="btn-gold !px-6 !py-2.5 !text-xs disabled:opacity-60" data-testid="settings-save">
                {saving ? "Saving…" : "Save Settings"}
              </button>
            )}
          </form>
        </Card>
      )}

      {tab === "audit" && hasPerm(me, "audit_view") && (
        <Card className="!p-0 overflow-hidden" testid="hr-audit">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead>
                <tr className="border-b border-navy-900/10 bg-ivory text-left">
                  {["When", "Action", "By", "Target", "Detail"].map((h) => (
                    <th key={h} className="px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {audit.map((a) => (
                  <tr key={a.id} className="border-b border-navy-900/5 last:border-0" data-testid={`audit-row-${a.id}`}>
                    <td className="whitespace-nowrap px-5 py-3 text-xs text-slate-500">{fmtDate(a.createdAt)} {fmtTime(a.createdAt)}</td>
                    <td className="px-5 py-3 font-semibold text-navy-900">{a.action}</td>
                    <td className="px-5 py-3 text-slate-600">{a.by}</td>
                    <td className="px-5 py-3 text-slate-600">{a.target || "—"}</td>
                    <td className="px-5 py-3 text-xs text-slate-400">{a.detail || "—"}</td>
                  </tr>
                ))}
                {audit.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-slate-400">No audit entries yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal open={modal === "holiday"} onClose={() => setModal(null)} title="Add Holiday">
        <form onSubmit={addHoliday} className="space-y-4" data-testid="holiday-form">
          <Field label="Name"><input required className={inputCls} value={hForm.name} onChange={(e) => setHForm({ ...hForm, name: e.target.value })} data-testid="holiday-name" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Date"><input type="date" required className={inputCls} value={hForm.date} onChange={(e) => setHForm({ ...hForm, date: e.target.value })} data-testid="holiday-date" /></Field>
            <Field label="Type">
              <select className={inputCls} value={hForm.type} onChange={(e) => setHForm({ ...hForm, type: e.target.value })} data-testid="holiday-type">
                {["National Holiday", "Festival Holiday", "Company Holiday", "Restricted Holiday"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={hForm.notifyAll} onChange={(e) => setHForm({ ...hForm, notifyAll: e.target.checked })} className="h-4 w-4 accent-gold" data-testid="holiday-notify" />
            Notify all employees
          </label>
          <button type="submit" disabled={saving} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="holiday-submit">{saving ? "Saving…" : "Add Holiday"}</button>
        </form>
      </Modal>

      <Modal open={modal === "leave-type"} onClose={() => setModal(null)} title="Add Leave Type">
        <form onSubmit={addType} className="space-y-4" data-testid="leave-type-form">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Code"><input required maxLength={4} className={inputCls} placeholder="PL" value={ltForm.code} onChange={(e) => setLtForm({ ...ltForm, code: e.target.value })} data-testid="lt-code" /></Field>
            <Field label="Annual Days"><input type="number" min="0" className={inputCls} value={ltForm.annual} onChange={(e) => setLtForm({ ...ltForm, annual: e.target.value })} data-testid="lt-annual" /></Field>
          </div>
          <Field label="Name"><input required className={inputCls} placeholder="Privilege Leave" value={ltForm.name} onChange={(e) => setLtForm({ ...ltForm, name: e.target.value })} data-testid="lt-name" /></Field>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={ltForm.paid} onChange={(e) => setLtForm({ ...ltForm, paid: e.target.checked })} className="h-4 w-4 accent-gold" data-testid="lt-paid" />
            Paid leave
          </label>
          <button type="submit" disabled={saving} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="lt-submit">{saving ? "Saving…" : "Create Leave Type"}</button>
        </form>
      </Modal>

      <Modal open={modal === "announcement"} onClose={() => setModal(null)} title="New Announcement">
        <form onSubmit={addAnnouncement} className="space-y-4" data-testid="announcement-form">
          <Field label="Title"><input required className={inputCls} value={aForm.title} onChange={(e) => setAForm({ ...aForm, title: e.target.value })} data-testid="ann-title" /></Field>
          <Field label="Message"><textarea required rows={4} className={inputCls} value={aForm.body} onChange={(e) => setAForm({ ...aForm, body: e.target.value })} data-testid="ann-body" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category">
              <select className={inputCls} value={aForm.category} onChange={(e) => setAForm({ ...aForm, category: e.target.value })} data-testid="ann-category">
                {["General", "Policy", "Event", "Urgent", "Celebration"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Audience">
              <select className={inputCls} value={aForm.audience} onChange={(e) => setAForm({ ...aForm, audience: e.target.value })} data-testid="ann-audience">
                <option>Everyone</option>
                {meta.departments.map((d) => <option key={d}>{d}</option>)}
                {meta.brands.map((b) => <option key={b}>{b}</option>)}
              </select>
            </Field>
          </div>
          <button type="submit" disabled={saving} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="ann-submit">{saving ? "Publishing…" : "Publish & Notify"}</button>
        </form>
      </Modal>
    </div>
  );
}

const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500";
